import { useSearchParentSurvey } from '@actions';
import { PaginationProps } from '@components/V4/Pagination';
import { SortOrder } from '@components/V4/Table/Table';
import { useDebounce, useQueryParamsState } from '@hooks';
import DetailsModal, {
  DetailsModalPage,
} from '@modules/Admin/components/DetailsModal/DetailsModal';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import ParentsHeader from './components/ParentsSurveyHeader';
import ParentsTable from './components/ParentsSurveyTable';

const useQuery = () => new URLSearchParams(useLocation().search);

//TODO: Add back if survey will be needed in the future
const ParentsSurvey = () => {
  const [enrollmentId, setEnrollmentId] = useState<string>();

  const [mentorId, setMentorId] = useState<string>();
  const [studentId, setStudentId] = useState<string>();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalPage, setModalPage] = useState<DetailsModalPage>('student');

  const [studentSearch, setStudentSearch] = useState('');
  const [mentorSearch, setMentorSearch] = useState('');
  const [parentSearch, setParentSearch] = useState('');

  const debouncedStudentSearch = useDebounce(studentSearch, 500);
  const debouncedMentorSearch = useDebounce(mentorSearch, 500);
  const debouncedParentSearch = useDebounce(parentSearch, 500);

  const query = useQuery();
  const limit = query.get('limit') || '10';
  const page = query.get('page') || '1';

  const course = query.get('course') || null;
  const student = query.get('student') || null;
  const mentor = query.get('mentor') || null;
  const parent = query.get('parent') || null;

  const sortBy = query.get('sortBy') || null;
  const sortOrder = (query.get('sortOrder') || null) as SortOrder;

  const [searchParams, setSearchParams] = useQueryParamsState<any>({
    page: page,
    limit: limit,
    course: course,
    student: student,
    mentor: mentor,
    parent: parent,
    sortBy: sortBy,
    sortOrder: sortOrder,
  });

  const { data: enrollments, isLoading: areEnrollmentsLoading } = useSearchParentSurvey({
    page: searchParams.page,
    limit: searchParams.limit,
    course: searchParams.course,
    student: searchParams.student,
    mentor: searchParams.mentor,
    parent: searchParams.parent,

    sortBy: searchParams.sortBy,
    sortOrder: searchParams.sortOrder,
  });

  const currentPageChangeHandler = (page: number) => {
    setSearchParams({
      page,
    });
  };

  const pageSizeChangeHandler = (size: number) => {
    setSearchParams({
      limit: size,
    });
  };

  const mentorModalOpenHandler = (mentorId: string) => {
    setModalPage('mentor');
    setMentorId(mentorId);
    setShowDetailsModal(true);
  };

  const enrollmentModalOpenHandler = (enrollmentId: string) => {
    setModalPage('enrollment');
    setEnrollmentId(enrollmentId);
    setShowDetailsModal(true);
  };

  const openStudentDetailsModalHandler = (studentId: string) => {
    setStudentId(studentId);
    setShowDetailsModal(true);
  };

  const closeSessionModalHandler = () => {
    setStudentId(undefined);
    setEnrollmentId(undefined);
    setShowDetailsModal(false);
    setModalPage(undefined);
  };

  const surveyModalOpenHandler = (enrollmentId: string) => {
    setModalPage('survey');
    setEnrollmentId(enrollmentId);
    setShowDetailsModal(true);
  };

  const pagination: PaginationProps = {
    pageSize: limit !== null ? parseInt(limit) : 10,
    total: enrollments?.total,
    currentPage: page !== null ? parseInt(page) : 1,
    onCurrentPageChange: currentPageChangeHandler,
    onPageSizeChange: pageSizeChangeHandler,
  };

  return (
    <section className="flex flex-col gap-8">
      <ParentsHeader
        title="Survey Feedback"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        studentSearch={studentSearch}
        setStudentSearch={setStudentSearch}
        mentorSearch={mentorSearch}
        parentSearch={parentSearch}
        setMentorSearch={setMentorSearch}
        setParentSearch={setParentSearch}
        debouncedStudentSearch={debouncedStudentSearch}
        debouncedMentorSearch={debouncedMentorSearch}
        debouncedParentSearch={debouncedParentSearch}
      />
      <ParentsTable
        enrollments={enrollments?.enrollments}
        areEnrollmentsLoading={areEnrollmentsLoading}
        pagination={pagination}
        sortBy={searchParams.sortBy}
        sortOrder={searchParams.sortOrder}
        setSearchParams={setSearchParams}
        mentorModalOpenHandler={mentorModalOpenHandler}
        enrollmentModalOpenHandler={enrollmentModalOpenHandler}
        openStudentDetailsModalHandler={openStudentDetailsModalHandler}
        surveyModalOpenHandler={surveyModalOpenHandler}
      />
      {showDetailsModal && (
        <DetailsModal
          mentorId={mentorId}
          studentId={studentId}
          enrollmentId={enrollmentId}
          page={modalPage}
          isOpen={true}
          onClose={closeSessionModalHandler}
        />
      )}
    </section>
  );
};

export default ParentsSurvey;
